import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { InvitacionPostulacion, RecomendacionInvitacion, RolInvitacion } from '../../../../core/models/invitaciones.models';
import { RolPlataforma } from '../../../../core/models/novedades.models';
import { InvitacionesService } from '../../../../core/services/invitaciones.service';
import { PostulacionesLogService } from '../../../../core/services/postulaciones-log.service';
import { TablePagination } from '../../../../shared/components/table-pagination/table-pagination';

@Component({
  selector: 'app-invitaciones-panel',
  imports: [CommonModule, FormsModule, TablePagination],
  templateUrl: './invitaciones-panel.html',
  styleUrl: './invitaciones-panel.css',
})
export class InvitacionesPanel implements OnChanges {
  @Input({ required: true }) rolSeleccionado: RolPlataforma = 'postulante';

  invitaciones: InvitacionPostulacion[] = [];
  mensajeAccion = '';
  modalRecomendacionVisible = false;
  invitacionActiva: InvitacionPostulacion | null = null;
  recomendacionForm: RecomendacionInvitacion = this.crearFormularioVacio();
  readonly opcionesCantidad = [10, 20, 30, 40, 50];
  cantidadPorPagina = 10;
  paginaActual = 1;

  constructor(
    private readonly invitacionesService: InvitacionesService,
    private readonly postulacionesLogService: PostulacionesLogService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['rolSeleccionado']) {
      this.cargarInvitaciones();
    }
  }

  get esRolInvitacion(): boolean {
    return this.rolSeleccionado === 'postulante' || this.rolSeleccionado === 'recomendador';
  }

  get esRolPostulante(): boolean {
    return this.rolSeleccionado === 'postulante';
  }

  get esRolRecomendador(): boolean {
    return this.rolSeleccionado === 'recomendador';
  }

  get encabezadoPanel(): string {
    if (this.esRolPostulante) return 'Invitaciones para participar';
    return this.esRolRecomendador ? 'Invitaciones para recomendacion' : 'Invitaciones recibidas';
  }

  get leadPanel(): string {
    if (this.esRolPostulante) {
      return 'Visualiza solicitudes para participar como coinvestigador, director adjunto, investigador patrocinante y otros roles asociados.';
    }

    return this.esRolRecomendador
      ? 'Acepta invitaciones para completar la carta de recomendacion, guardar avances y enviarla cuando este lista.'
      : 'Acepta o rechaza tu participacion en procesos de postulacion y deja registro de la respuesta.';
  }

  get mensajeSinInvitaciones(): string {
    if (this.esRolPostulante) {
      return 'Cuando recibas invitaciones para participar en otras postulaciones, apareceran aqui.';
    }

    if (this.esRolRecomendador) {
      return 'Cuando recibas nuevas invitaciones para recomendar postulaciones, apareceran aqui.';
    }

    return 'Cuando recibas nuevas invitaciones para colaborar en postulaciones, apareceran aqui.';
  }

  get invitacionesPaginadas(): InvitacionPostulacion[] {
    const paginaTotal = this.totalPaginas;
    if (this.paginaActual > paginaTotal) {
      this.paginaActual = paginaTotal;
    }

    const inicio = (this.paginaActual - 1) * this.cantidadPorPagina;
    return this.invitaciones.slice(inicio, inicio + this.cantidadPorPagina);
  }

  get totalPaginas(): number {
    return Math.max(1, Math.ceil(this.invitaciones.length / this.cantidadPorPagina));
  }

  cambiarPagina(pagina: number): void {
    this.paginaActual = pagina;
  }

  cambiarCantidadDesdePaginador(cantidad: number): void {
    this.cantidadPorPagina = cantidad;
    this.paginaActual = 1;
  }

  responder(item: InvitacionPostulacion, estadoRespuesta: 'aceptada' | 'rechazada'): void {
    if (!this.esRolInvitacion || item.estadoRespuesta !== 'pendiente') return;

    if (this.esRolRecomendador && estadoRespuesta === 'aceptada') {
      this.abrirModalRecomendacion(item);
      return;
    }

    this.invitacionesService.responderInvitacion(item.id, this.rolSeleccionado as RolInvitacion, estadoRespuesta).subscribe((respuesta) => {
      if (!respuesta) {
        this.mensajeAccion = 'No fue posible registrar la respuesta de la invitacion.';
        return;
      }

      this.mensajeAccion =
        estadoRespuesta === 'aceptada'
          ? `Invitacion ${respuesta.idPostulacion} aceptada y asociada al proceso.`
          : this.esRolRecomendador
            ? `Invitacion ${respuesta.idPostulacion} marcada como no recomendada.`
            : `Invitacion ${respuesta.idPostulacion} rechazada y mantenida como registro historico.`;

      this.postulacionesLogService.registrar({
        timestamp: new Date().toISOString(),
        rol: this.rolSeleccionado,
        idPostulacion: respuesta.idPostulacion,
        folio: respuesta.folio,
        accion: estadoRespuesta === 'aceptada' ? 'aceptar_invitacion' : 'rechazar_invitacion',
        detalle: this.detalleRespuestaInvitacion(respuesta, estadoRespuesta),
      });

      this.cargarInvitaciones();
    });
  }

  abrirModalRecomendacion(item: InvitacionPostulacion): void {
    if (!this.esRolRecomendador || item.cartaEnviada) return;

    this.invitacionActiva = item;
    this.recomendacionForm = item.recomendacion ? { ...item.recomendacion } : this.crearFormularioVacio();
    this.modalRecomendacionVisible = true;

    if (item.cartaGuardada && !item.cartaEnviada) {
      this.postulacionesLogService.registrar({
        timestamp: new Date().toISOString(),
        rol: this.rolSeleccionado,
        idPostulacion: item.idPostulacion,
        folio: item.folio,
        accion: 'editar_recomendacion',
        detalle: 'Reabre borrador de recomendacion para continuar su edicion.',
      });
    }
  }

  cerrarModalRecomendacion(): void {
    this.modalRecomendacionVisible = false;
    this.invitacionActiva = null;
    this.recomendacionForm = this.crearFormularioVacio();
  }

  guardarRecomendacion(): void {
    if (!this.invitacionActiva) return;

    this.invitacionesService.guardarRecomendacion(this.invitacionActiva.id, this.recomendacionPayload()).subscribe((respuesta) => {
      if (!respuesta) {
        this.mensajeAccion = 'No fue posible guardar el formulario de recomendacion.';
        return;
      }

      this.postulacionesLogService.registrar({
        timestamp: new Date().toISOString(),
        rol: this.rolSeleccionado,
        idPostulacion: respuesta.idPostulacion,
        folio: respuesta.folio,
        accion: 'guardar_recomendacion',
        detalle: 'Guarda borrador del formulario de recomendacion.',
      });

      this.mensajeAccion = `Borrador de recomendacion guardado para ${respuesta.idPostulacion}.`;
      this.cerrarModalRecomendacion();
      this.cargarInvitaciones();
    });
  }

  enviarRecomendacion(): void {
    if (!this.invitacionActiva) return;

    this.invitacionesService.enviarRecomendacion(this.invitacionActiva.id, this.recomendacionPayload()).subscribe((respuesta) => {
      if (!respuesta) {
        this.mensajeAccion = 'No fue posible enviar la carta de recomendacion.';
        return;
      }

      this.postulacionesLogService.registrar({
        timestamp: new Date().toISOString(),
        rol: this.rolSeleccionado,
        idPostulacion: respuesta.idPostulacion,
        folio: respuesta.folio,
        accion: 'enviar_recomendacion',
        detalle: 'Envia carta de recomendacion y la asocia a la postulacion.',
      });

      this.mensajeAccion = `Carta de recomendacion enviada para ${respuesta.idPostulacion}.`;
      this.cerrarModalRecomendacion();
      this.cargarInvitaciones();
    });
  }

  etiquetaEstado(item: InvitacionPostulacion): string {
    if (this.esRolRecomendador && item.cartaGuardada && !item.cartaEnviada && item.estadoRespuesta === 'pendiente') {
      return 'Borrador';
    }
    if (item.estadoRespuesta === 'aceptada') return 'Aceptada';
    if (item.estadoRespuesta === 'rechazada') {
      if (this.esRolRecomendador) return 'No recomendado';
      return 'Rechazada';
    }
    return 'Pendiente';
  }

  claseEstado(item: InvitacionPostulacion): string {
    if (item.estadoRespuesta === 'aceptada') return 'badge-success';
    if (item.estadoRespuesta === 'rechazada') return 'badge-danger';
    if (this.esRolRecomendador && item.cartaGuardada && !item.cartaEnviada) return 'badge-info';
    return 'badge-warning';
  }

  puedeEditarBorrador(item: InvitacionPostulacion): boolean {
    return this.esRolRecomendador && !!item.cartaGuardada && !item.cartaEnviada && item.estadoRespuesta === 'pendiente';
  }

  private recomendacionPayload(): RecomendacionInvitacion {
    return {
      resumen: this.recomendacionForm.resumen.trim(),
      fortalezas: this.recomendacionForm.fortalezas.trim(),
      recomendacionFinal: this.recomendacionForm.recomendacionFinal.trim(),
    };
  }

  private cargarInvitaciones(): void {
    if (!this.esRolInvitacion) {
      this.invitaciones = [];
      this.paginaActual = 1;
      return;
    }

    this.invitacionesService.getInvitacionesPorRol(this.rolSeleccionado as RolInvitacion).subscribe((items) => {
      this.invitaciones = items;
      this.paginaActual = 1;
    });
  }

  private crearFormularioVacio(): RecomendacionInvitacion {
    return {
      resumen: '',
      fortalezas: '',
      recomendacionFinal: '',
    };
  }

  private detalleRespuestaInvitacion(
    invitacion: InvitacionPostulacion,
    estadoRespuesta: 'aceptada' | 'rechazada'
  ): string {
    const rol = this.esRolPostulante
      ? invitacion.tipoParticipacion ?? 'participacion solicitada'
      : this.esRolRecomendador
        ? 'recomendacion'
        : 'participacion';

    return estadoRespuesta === 'aceptada'
      ? `Acepta invitacion de ${rol} y queda asociado al proceso.`
      : `Rechaza invitacion de ${rol} y mantiene registro historico.`;
  }
}
